import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { AppBar, Toolbar, Typography, Tabs, Tab, Box, IconButton, Avatar, Menu, MenuItem, ListItemIcon, Divider } from '@mui/material';
import { useLocation, useNavigate } from 'react-router-dom';
import { useState, useEffect } from 'react';
import axios from 'axios';
import { useTheme } from '../contexts/ThemeContext';
import { useAuth } from '../contexts/AuthContext';
import { hasAnyRole } from '../config/rbac';
const API_URL = process.env.REACT_APP_API_URL || '/api';
const buildProfilePictureThumbnailUrl = (driveFileId) => `${API_URL}/onboarding/profile-picture/${driveFileId}?w=96`;
import Brightness4Icon from '@mui/icons-material/Brightness4';
import Brightness7Icon from '@mui/icons-material/Brightness7';
import LogoutIcon from '@mui/icons-material/Logout';
import PersonIcon from '@mui/icons-material/Person';
import SettingsIcon from '@mui/icons-material/Settings';
import LockResetIcon from '@mui/icons-material/LockReset';
const drawerWidth = 220;
const pageTitles = {
    '/hr-dashboard': 'Dashboard',
    '/employees': 'Employees List',
    '/candidates': 'Candidates',
    '/dept-designation-master': 'Dept & Designation Master',
    '/salary-revision': 'Salary revision',
    '/employee-letters': 'Employee Letters',
    '/escalations': 'Escalations',
    '/grievances': 'Grievances',
    '/recruitment': 'All Requisitions',
    '/applicants': 'All Applicants',
    '/scoring': 'Recruitment Scoring',
    '/onboarding': 'Onboarding',
    '/training-page': 'Trainings',
    '/outing': 'Outing / Event',
    '/attendance': 'Attendance',
    '/checklist-delegation': 'Check List & Delegation',
    '/requisition': 'Requisition',
    '/confirmations': 'Confirmations',
    '/salary-sheet': 'Salary Sheet',
    '/pms': 'Performance Management System',
    '/exits': 'Exit Dashboard',
    '/exits/dashboard': 'Exit Dashboard',
    '/new-exit': 'New Exit',
    '/exits/update': 'Update Exit',
    '/company-orientation': 'Company Orientation',
    '/dept-orientation': 'Department Orientation',
    '/profile': 'Profile',
    '/configuration': 'Configruation',
};
// ─── Tab definitions ──────────────────────────────────────────────────────────
const OUTING_TABS = [
    { label: 'HR', value: 'HR' },
    { label: 'Management Approvals', value: 'management' },
    { label: 'Scheduled & Completed', value: 'outings-view' },
    { label: 'Employee Feedback', value: 'employee-feedback' },
    { label: 'Scorecard', value: 'scorecard' },
];
const TRAINING_TABS = [
    { label: 'HR', value: 'HR' },
    // Manager Evaluation & Skill Gap tabs removed from nav per request (2026-10-13) — keep commented, not deleted.
    // { label: 'Manager Evaluation',  value: 'manager' },
    // { label: 'Skill Gap',           value: 'skillgap' },
    { label: 'Management', value: 'management' },
    { label: 'Employee', value: 'employee' },
    { label: 'Training Delivery', value: 'delivery' },
    { label: 'Scorecard', value: 'scorecard' },
];
const TRAINING_HR_SUBTABS = [
    { label: 'Capability', value: 'capability' },
    { label: 'Topics', value: 'topics' },
    { label: 'Scheduling', value: 'scheduling' },
];
const TRAINING_EMP_SUBTABS = [
    { label: 'Assessment', value: 'assessment' },
    { label: 'Feedback', value: 'feedback' },
];
const PMS_TABS = [
    { label: 'KPI & Targets', value: 'kpi' },
    { label: 'Hygiene Factors', value: 'hygiene' },
    { label: 'Growth', value: 'growth' },
    { label: 'Final Performance', value: 'summary' },
];
// ─── Styles ───────────────────────────────────────────────────────────────────
const BLUE_TAB_STYLES = {
    minHeight: 40,
    '& .MuiTab-root': {
        color: 'rgba(255,255,255,0.7)',
        fontWeight: 600,
        fontSize: '0.78rem',
        textTransform: 'none',
        minHeight: 40,
        px: 2.5,
        '&.Mui-selected': { color: '#ffffff' },
    },
};
const WHITE_TAB_STYLES = {
    minHeight: 40,
    '& .MuiTab-root': {
        color: 'rgba(0,0,0,0.45)',
        fontWeight: 600,
        fontSize: '0.78rem',
        textTransform: 'none',
        minHeight: 40,
        px: 2.5,
        '&.Mui-selected': { color: '#1d1d1d' },
    },
};
const TOOLBAR_H = 56;
const TAB_ROW_H = 40;
// ─── Component ────────────────────────────────────────────────────────────────
export default function Navbar({ actionButton } = {}) {
    const nav = useNavigate();
    const loc = useLocation();
    const { darkMode, toggleDarkMode } = useTheme();
    const { user, logout } = useAuth();
    const handleLogout = () => {
        logout();
        nav('/login', { replace: true });
    };
    const [profileMenuAnchor, setProfileMenuAnchor] = useState(null);
    const profileMenuOpen = Boolean(profileMenuAnchor);
    const closeProfileMenu = () => setProfileMenuAnchor(null);
    const initials = (user?.name || '?')
        .split(' ')
        .filter(Boolean)
        .slice(0, 2)
        .map((n) => n[0]?.toUpperCase())
        .join('');
    // Same self-service DP (profile picture) the Profile page's avatar shows —
    // fetched independently here since the Navbar is mounted on every page and
    // AuthUser (the login session) never carries it, only the Onboarding record
    // does. Best-effort: a missing/failed lookup just falls back to initials.
    const [avatarUrl, setAvatarUrl] = useState(null);
    useEffect(() => {
        if (!user?.email) {
            setAvatarUrl(null);
            return;
        }
        let cancelled = false;
        axios.get(`${API_URL}/onboarding/by-email`, { params: { email: user.email, empId: user.employeeId || undefined, name: user.name } })
            .then((res) => {
            if (cancelled)
                return;
            const pic = res.data?.data?.profilePicture;
            setAvatarUrl(pic?.driveFileId ? buildProfilePictureThumbnailUrl(pic.driveFileId) : null);
        })
            .catch(() => { if (!cancelled)
            setAvatarUrl(null); });
        return () => { cancelled = true; };
    }, [user?.email]);
    const sp = new URLSearchParams(loc.search);
    const activeTab = sp.get('tab') || '';
    const activeHrSub = sp.get('hrSub') || 'capability';
    const activeEmpSub = sp.get('empSub') || 'assessment';
    const isOutingPage = loc.pathname === '/outing';
    const isTrainingPage = loc.pathname === '/training-page';
    const isPmsPage = loc.pathname === '/pms';
    const showTrainingHrSub = isTrainingPage && activeTab === 'HR';
    const showTrainingEmpSub = isTrainingPage && activeTab === 'employee';
    const pageTitle = pageTitles[loc.pathname] || 'HR Portal';
    // Blue tab row — inside AppBar (only outing/pms still blue)
    const renderBlueTabRow = (tabs, activeValue, onChange) => (_jsx(Box, { sx: { borderTop: '1px solid rgba(255,255,255,0.2)', px: 2 }, children: _jsx(Tabs, { value: activeValue || tabs[0].value, onChange: (_, v) => onChange(v), textColor: "inherit", TabIndicatorProps: { style: { backgroundColor: '#ffffff', height: 3 } }, sx: BLUE_TAB_STYLES, children: tabs.map(t => _jsx(Tab, { label: t.label, value: t.value }, t.value)) }) }));
    // White tab row — fixed strip outside AppBar
    const renderWhiteTabRow = (tabs, activeValue, onChange, topOffset, rightContent) => (_jsxs(Box, { sx: {
            position: 'fixed', top: topOffset, left: drawerWidth, right: 0, zIndex: 1099,
            backgroundColor: '#ffffff', borderBottom: '1px solid #e5e7eb',
            boxShadow: '0 1px 3px rgba(0,0,0,0.07)', px: 2,
            display: 'flex', alignItems: 'center', justifyContent: 'space-between',
        }, children: [_jsx(Tabs, { value: activeValue || tabs[0].value, onChange: (_, v) => onChange(v), textColor: "inherit", TabIndicatorProps: { style: { backgroundColor: '#4A6427', height: 3 } }, sx: WHITE_TAB_STYLES, children: tabs.map(t => _jsx(Tab, { label: t.label, value: t.value }, t.value)) }), rightContent] }));
    const row1Top = TOOLBAR_H;
    const row2Top = TOOLBAR_H + TAB_ROW_H;
    return (_jsxs(_Fragment, { children: [_jsx(AppBar, { position: "fixed", sx: {
                    width: `calc(100% - ${drawerWidth}px)`, ml: `${drawerWidth}px`,
                    backgroundColor: '#4A6427', boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
                    height: TOOLBAR_H,
                }, children: _jsxs(Toolbar, { sx: { px: 4, minHeight: `${TOOLBAR_H}px !important`, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }, children: [_jsx(Typography, { variant: "h6", fontWeight: 700, color: "white", children: pageTitle }), _jsxs(Box, { sx: { display: 'flex', alignItems: 'center', gap: 2 }, children: [_jsx(Box, { onClick: toggleDarkMode, sx: {
                                        cursor: 'pointer',
                                        display: 'flex',
                                        alignItems: 'center',
                                        gap: 1,
                                        padding: '4px 8px',
                                        borderRadius: '4px',
                                        '&:hover': { backgroundColor: 'rgba(255,255,255,0.1)' }
                                    }, children: darkMode ? (_jsx(Brightness7Icon, { sx: { color: 'white', fontSize: 20 } })) : (_jsx(Brightness4Icon, { sx: { color: 'white', fontSize: 20 } })) }), _jsx(IconButton, { onClick: (e) => setProfileMenuAnchor(e.currentTarget), title: user ? user.name : 'Account', sx: { p: 0.5 }, children: _jsx(Avatar, { src: avatarUrl || undefined, sx: {
                                            width: 34, height: 34, fontSize: 13, fontWeight: 700,
                                            bgcolor: 'rgba(255,255,255,0.15)', color: '#fff', border: '1px solid rgba(255,255,255,0.3)',
                                        }, children: initials }) })] }), _jsxs(Menu, { anchorEl: profileMenuAnchor, open: profileMenuOpen, onClose: closeProfileMenu, anchorOrigin: { vertical: 'bottom', horizontal: 'right' }, transformOrigin: { vertical: 'top', horizontal: 'right' }, children: [user && (_jsxs(Box, { sx: { px: 2, py: 1, minWidth: 180 }, children: [_jsx(Typography, { sx: { fontWeight: 700, fontSize: 14 }, children: user.name }), _jsx(Typography, { sx: { fontSize: 12, color: 'text.secondary' }, children: user.email })] })), _jsx(Divider, {}), _jsxs(MenuItem, { onClick: () => { closeProfileMenu(); nav('/profile'); }, children: [_jsx(ListItemIcon, { children: _jsx(PersonIcon, { fontSize: "small" }) }), "Profile"] }), _jsxs(MenuItem, { onClick: () => { closeProfileMenu(); nav('/configuration'); }, children: [_jsx(ListItemIcon, { children: _jsx(SettingsIcon, { fontSize: "small" }) }), "Configuration"] }), _jsxs(MenuItem, { onClick: () => { closeProfileMenu(); nav('/configuration?openPassword=1'); }, children: [_jsx(ListItemIcon, { children: _jsx(LockResetIcon, { fontSize: "small" }) }), "Change Password"] }), _jsx(Divider, {}), _jsxs(MenuItem, { onClick: () => { closeProfileMenu(); handleLogout(); }, children: [_jsx(ListItemIcon, { children: _jsx(LogoutIcon, { fontSize: "small", color: "error" }) }), "Logout"] })] })] }) }), isOutingPage && renderWhiteTabRow(hasAnyRole(['Admin', 'HR', 'Management']) ? OUTING_TABS : OUTING_TABS.filter(t => t.value !== 'management'), activeTab || 'HR', v => nav(`/outing?tab=${v}`), row1Top, (activeTab || 'HR') === 'HR' ? actionButton : undefined), isTrainingPage && renderWhiteTabRow(TRAINING_TABS, activeTab || 'HR', v => nav(`/training-page?tab=${v}`), row1Top), showTrainingHrSub && renderWhiteTabRow(TRAINING_HR_SUBTABS, activeHrSub, v => nav(`/training-page?tab=HR&hrSub=${v}`), row2Top), showTrainingEmpSub && renderWhiteTabRow(TRAINING_EMP_SUBTABS, activeEmpSub, v => nav(`/training-page?tab=employee&empSub=${v}`), row2Top), isPmsPage && renderWhiteTabRow(PMS_TABS, activeTab || 'kpi', v => nav(`/pms?tab=${v}`), row1Top)] }));
}
